/**
 * The typed client generated from the API's OpenAPI spec (M0-25).
 *
 * The same spec and the same generator as the customer app, because it is the
 * same API — the staff console just reaches the `/api/v1/admin/*` half of it.
 * `schema.d.ts` is regenerated by `npm run generate:client` and committed, so
 * CI's drift check covers this app too.
 *
 * ⚠️ `VITE_API_BASE_URL`, deliberately **not** `VITE_STAFF_API_BASE_URL`. The
 * identity variables are staff-prefixed because pointing them at the wrong
 * directory is a real hazard; the API origin is genuinely the same value for
 * both apps, and inventing a second name for one value invites the two
 * drifting apart in a deploy.
 */
import createClient from 'openapi-fetch';
import type { paths } from './generated/schema';
import { getCurrentAccessToken } from '@/lib/authToken';
import { reportApiUnavailable, reportAuthRequired } from '@/lib/maintenance';

// `location.origin`, not `''` — openapi-fetch builds a real `URL` internally
// and `new URL('/api/v1/admin/me', '')` throws. Same reasoning as the
// customer app's client.
const baseUrl = import.meta.env.VITE_API_BASE_URL || window.location.origin;

export const apiClient = createClient<paths>({
  baseUrl,
  // Re-reads `globalThis.fetch` per call rather than capturing it at module
  // load, so a test that stubs fetch after import is actually honoured.
  fetch: (input) => globalThis.fetch(input),
});

apiClient.use({
  async onRequest({ request }) {
    let token: string | null;
    try {
      token = await getCurrentAccessToken();
    } catch (error) {
      // openapi-fetch does not wrap `onRequest` in try/catch, so a throw here
      // (`acquireTokenRedirect` itself rejecting — e.g. msal-browser's
      // `interaction_in_progress` — or any other token-fetch failure) would
      // otherwise skip the onResponse/onError middleware below entirely
      // instead of being reported.
      reportApiUnavailable();
      throw error;
    }
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  },
});

// The proxy/gateway statuses, never something OUR code returns on purpose —
// NOT 503, which `JobPhotoEndpoints`, `AddressEndpoints`, `MapsEndpoints`, and
// `TechnicianProfileEndpoints` all hand-write deliberately for a scoped
// feature being unavailable (storage, geocoding, map tiles not configured),
// so treating it as a platform outage would flag those as maintenance too.
// Nothing in this API hand-writes a 502 or a 504. Confirmed against the
// actual local dev proxy: with the API not running, Vite's own proxy answers
// `/api/*` with a 502, not a network-level failure — and Azure Static Web
// Apps' reverse proxy does the same thing in front of a torn-down Container
// App (M0-09). A thrown fetch (`onError`, below) is the OTHER half of
// "unreachable": no proxy in front at all, or the proxy itself unreachable.
const GATEWAY_STATUSES = new Set([502, 504]);

// M1-29: reports what the maintenance detector needs, never handles the
// response itself — every call site still sees its own error/status exactly
// as before. A 401 goes to `reportAuthRequired` (routes to sign-in, never
// maintenance); a network-level failure or a gateway status goes to
// `reportApiUnavailable`, which confirms via `/health` before showing
// anything (see `lib/maintenance.ts` for why one bad response is not enough
// on its own). A business 500 (or 403, this console's own permission
// refusal) deliberately reports nothing.
apiClient.use({
  onResponse({ response }) {
    if (response.status === 401) {
      reportAuthRequired();
    } else if (GATEWAY_STATUSES.has(response.status)) {
      reportApiUnavailable();
    }
    return response;
  },
  onError() {
    reportApiUnavailable();
    // Returning nothing lets the original error propagate to the caller
    // unchanged — this middleware only ever OBSERVES.
    return undefined;
  },
});
